import { useState, useEffect, useCallback } from "react";
import { trackThemeChange } from "@/utils/analytics";

const STORAGE_KEY = "sewar-theme";

type Theme = "light" | "dark";

/**
 * useTheme — manages light/dark theme.
 * Persists choice in localStorage.
 * Falls back to system preference on first visit.
 */
export const useTheme = () => {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === "undefined") return "light";
    const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
    if (stored === "dark" || stored === "light") return stored;
    return "light";
  });

  // Sync the .dark class on <html>
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const nextTheme = prev === "dark" ? "light" : "dark";
      trackThemeChange(nextTheme);
      return nextTheme;
    });
  }, []);

  const isDark = theme === "dark";

  return { theme, isDark, toggleTheme, setTheme: setThemeState };
};
